import type {
	ScreenEventPayload,
	TrackEventPayload,
	UIEventPayload,
} from '@atlassiansox/analytics-web-client/types';
import { useTenantData } from '@dev-agents/tenant-context';
import { useCallback } from 'react';

import { useAnalyticsData } from './analytics-data-context';
import { useAIAnalyticsWebClient, useAnalyticsWebClient } from './web-client-context';

export const useRovoDevWebAnalyticsClient = () => {
	const { tenantContexts } = useTenantData();
	const currentTenantContext = tenantContexts[0];
	const { sendUIEvent, sendScreenEvent, sendTrackEvent } = useAnalyticsWebClient();
	const analyticsData = useAnalyticsData();

	const fireUiEvent = useCallback(
		(eventPayload: UIEventPayload) => {
			const commonAttrs = {
				orgId: currentTenantContext.orgId,
				...analyticsData,
			};
			const extendedPayload = {
				...eventPayload,
				attributes: {
					...eventPayload.attributes,
					...commonAttrs,
				},
			};
			sendUIEvent(extendedPayload);
		},
		[currentTenantContext.orgId, analyticsData, sendUIEvent],
	);

	const fireScreenEvent = useCallback(
		(eventPayload: ScreenEventPayload) => {
			const commonAttrs = {
				orgId: currentTenantContext.orgId,
				...analyticsData,
			};
			const extendedPayload = {
				...eventPayload,
				attributes: {
					...eventPayload.attributes,
					...commonAttrs,
				},
			};
			sendScreenEvent(extendedPayload);
		},
		[currentTenantContext.orgId, analyticsData, sendScreenEvent],
	);

	const fireTrackEvent = useCallback(
		(eventPayload: TrackEventPayload) => {
			const commonAttrs = {
				orgId: currentTenantContext.orgId,
				...analyticsData,
			};
			const extendedPayload = {
				...eventPayload,
				attributes: {
					...eventPayload.attributes,
					...commonAttrs,
				},
			};
			sendTrackEvent(extendedPayload);
		},
		[currentTenantContext.orgId, analyticsData, sendTrackEvent],
	);

	return {
		fireUiEvent,
		fireScreenEvent,
		fireTrackEvent,
	};
};

/**
 * Fires track events through the dedicated AI Unified client
 * (`product: 'devAi'`). Mirrors the orgId + analyticsData enrichment of
 * `useRovoDevWebAnalyticsClient`.
 */
export const useDevAIWebAnalyticsClient = () => {
	const { tenantContexts } = useTenantData();
	const currentTenantContext = tenantContexts[0];
	const { sendTrackEvent } = useAIAnalyticsWebClient();
	const analyticsData = useAnalyticsData();

	const fireTrackEvent = useCallback(
		(eventPayload: TrackEventPayload) => {
			const commonAttrs = {
				orgId: currentTenantContext.orgId,
				...analyticsData,
			};
			const extendedPayload = {
				...eventPayload,
				attributes: {
					...eventPayload.attributes,
					...commonAttrs,
				},
			};
			sendTrackEvent(extendedPayload);
		},
		[currentTenantContext.orgId, analyticsData, sendTrackEvent],
	);

	return { fireTrackEvent };
};
